/**
 * Politique de mot de passe, reprise à l'identique de celle du pool Cognito
 * (`infra/lib/auth-stack.ts`).
 *
 * Elle est dupliquée ici à dessein : Cognito ne l'expose pas, et laisser
 * quelqu'un composer un mot de passe pour se le voir refuser après envoi est
 * la façon la plus sûre de le faire renoncer. Les deux définitions doivent
 * évoluer ensemble — d'où le test qui fige les valeurs.
 */

export const MIN_PASSWORD_LENGTH = 12;

export interface PasswordRule {
  readonly label: string;
  readonly met: boolean;
}

/**
 * Évalue les critères sur une saisie en cours.
 *
 * Les quatre critères sont toujours renvoyés, y compris ceux déjà satisfaits :
 * afficher la liste complète dit ce qui est attendu, alors que ne montrer que
 * les manquements transforme la saisie en devinette.
 */
export function passwordRules(value: string): readonly PasswordRule[] {
  return [
    {
      label: `${MIN_PASSWORD_LENGTH} caractères au moins`,
      met: value.length >= MIN_PASSWORD_LENGTH,
    },
    { label: 'une majuscule', met: /\p{Lu}/u.test(value) },
    { label: 'une minuscule', met: /\p{Ll}/u.test(value) },
    { label: 'un chiffre', met: /\d/.test(value) },
  ];
}

export function isPasswordValid(value: string): boolean {
  return passwordRules(value).every((rule) => rule.met);
}

/**
 * Contrôle de forme d'une adresse e-mail, volontairement permissif.
 *
 * Le but n'est pas de valider une adresse — seul un e-mail reçu le prouve — mais
 * d'éviter un aller-retour réseau sur une saisie manifestement incomplète.
 */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}
